import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Copy, Download, Key, LifeBuoy, Lock, Mail, ShieldCheck, Smartphone, Terminal } from 'lucide-react';
import { isConsolePath } from '../config/devOnly';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { getAdminProductConfig } from '../config/productConfig';
import { NodeNetwork } from '../components/NodeNetwork';
import { staffApi } from '../services/staffApi';

// Neutral message shown for both a successful reset-code send AND an unknown
// email, so the UI never reveals whether an account exists.
const NEUTRAL_RESET_MESSAGE = 'If an account exists for that email, a reset code has been sent.';

// The admin pool's account recovery is admin_only (no self-service forgot-
// password), so Cognito answers EVERY address — known or not — with
// NotAuthorizedException "Contact administrator to reset password.". Same
// message for all, so it reveals nothing about which accounts exist.
const ADMIN_RESET_MESSAGE = 'Ask your admin to reset your password.';
const isSelfServiceRecoveryDisabled = (err: any) =>
  (err?.code || err?.name) === 'NotAuthorizedException' || /contact administrator/i.test(String(err?.message || ''));

type ResetStep = 'idle' | 'request' | 'confirm';

function LoginPage() {
  const product = getAdminProductConfig();
  // Per-product theme classes (Ruby Till uses red, others fall back to the
  // platform's default admin-purple). Falling back keeps the behaviour
  // identical for cafemaster and terrakron — only agenticpos overrides.
  const btnClass = product.btnClass ?? 'bg-admin-primary hover:bg-admin-secondary';
  const inputFocusClass = product.inputFocusClass ?? 'focus:border-admin-primary';
  const accentTextClass = product.accentTextClass ?? 'text-admin-primary';
  const isRubyTill = product.id === 'agenticpos';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Forgot-password flow state (self-contained; does not touch the
  // login/MFA/new-password flows above).
  const [resetStep, setResetStep] = useState<ResetStep>('idle');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetInfoMessage, setResetInfoMessage] = useState<string | null>(null);
  const [resetErrorMessage, setResetErrorMessage] = useState<string | null>(null);
  const [signinNotice, setSigninNotice] = useState<string | null>(null);

  // Lost-authenticator recovery (staff-service lib/mfa.ts has the model).
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [codesSaved, setCodesSaved] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);

  const {
    login, verifyMFA, completeNewPassword, verifyMfaSetup, forgotPassword, confirmForgotPassword,
    verifyEmailCode, acknowledgeRecoveryCodes,
    isLoading, isAuthenticated, mfaRequired, newPasswordRequired, mfaSetupRequired, mfaSetupSecret,
    emailCodeRequired, emailCodeDestination, recoveryCodes, email: authEmail, error
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Navigate only once sign-in has truly finished (every step, including
  // saving recovery codes) — the handlers below never navigate themselves.
  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const accountEmail = email || authEmail || '';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch (err) {
      // Error handled by context
    }
  };

  const handleMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verifyMFA(mfaCode);
    } catch (err) {
      // Error handled by context
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    if (newPassword.length < 8) return;
    try {
      await completeNewPassword(newPassword);
      // Don't navigate - may need MFA setup next
    } catch (err) {
      // Error handled by context
    }
  };

  const handleMfaSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verifyMfaSetup(setupCode);
    } catch (err) {
      // Error handled by context
    }
  };

  // Lost authenticator: password (already typed) + one recovery code opens a
  // recovery; signing in again then asks for a one-time email code.
  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError(null);
    setRecoveryBusy(true);
    try {
      await staffApi.security.recover({ email: accountEmail, password, code: recoveryCode });
      setShowRecovery(false);
      setRecoveryCode('');
      await login(accountEmail, password);
    } catch (err: any) {
      setRecoveryError(err?.message || "Those details didn't match.");
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handleEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verifyEmailCode(emailCode);
    } catch {
      // Error handled by context
    }
  };

  const codesText = () => [
    `${product.adminTitle} Super Admin — recovery codes`,
    `Account: ${accountEmail}`,
    `Created: ${new Date().toLocaleString()}`,
    '',
    'Each code works once. Keep them somewhere safe and separate from your phone.',
    '',
    ...(recoveryCodes || []),
  ].join('\n');

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codesText());
      setCodesCopied(true);
    } catch {
      setCodesCopied(false);
    }
  };

  const downloadCodes = () => {
    const blob = new Blob([codesText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rubytill-super-admin-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openForgotPassword = () => {
    setResetEmail(email);
    setResetErrorMessage(null);
    setResetInfoMessage(null);
    setSigninNotice(null);
    setResetStep('request');
  };

  const backToSignIn = () => {
    setResetStep('idle');
    setResetCode('');
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetErrorMessage(null);
    setResetInfoMessage(null);
  };

  // Sends (or resends) the reset code. Never reveals whether the email
  // belongs to an account - an unknown-email failure shows the same
  // neutral message as success and still advances to step 2.
  const sendResetCode = async (targetEmail: string) => {
    setResetErrorMessage(null);
    try {
      await forgotPassword(targetEmail);
      setResetInfoMessage(NEUTRAL_RESET_MESSAGE);
      setResetStep('confirm');
    } catch (err: any) {
      if (err?.code === 'UserNotFoundException') {
        setResetInfoMessage(NEUTRAL_RESET_MESSAGE);
        setResetStep('confirm');
        return;
      }
      if (isSelfServiceRecoveryDisabled(err)) {
        setResetErrorMessage(ADMIN_RESET_MESSAGE);
        return;
      }
      setResetErrorMessage(err?.message || 'Failed to send reset code');
    }
  };

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendResetCode(resetEmail);
  };

  const handleResendCode = async () => {
    await sendResetCode(resetEmail);
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) return;
    if (resetNewPassword.length < 8) return;
    setResetErrorMessage(null);
    try {
      await confirmForgotPassword(resetEmail, resetCode, resetNewPassword);
      setEmail(resetEmail);
      setPassword('');
      setResetStep('idle');
      setResetCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setResetInfoMessage(null);
      setSigninNotice('Your password has been reset. Please sign in with your new password.');
    } catch (err: any) {
      setResetErrorMessage(err?.message || 'Failed to reset password');
    }
  };

  // Determine which form to show
  const renderForm = () => {
    if (resetStep === 'request') {
      return (
        <form onSubmit={handleSendResetCode} className="space-y-4">
          <div className="text-center mb-4">
            <Mail className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Reset Password</h2>
            <p className="text-sm text-stone-400">
              Enter your account email and we'll send a reset code
            </p>
          </div>

          {resetErrorMessage && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{resetErrorMessage}</p>
            </div>
          )}

          <div>
            <label htmlFor="resetEmail" className="block text-sm font-medium text-stone-300 mb-1">
              Email
            </label>
            <input
              id="resetEmail"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder={product.emailPlaceholder}
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              autoFocus
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !resetEmail}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </button>

          <button
            type="button"
            onClick={backToSignIn}
            className={`w-full text-center text-sm text-stone-400 hover:${accentTextClass} transition-colors`}
          >
            Back to sign in
          </button>
        </form>
      );
    }

    if (resetStep === 'confirm') {
      const passwordsMatch = resetNewPassword.length > 0 && resetNewPassword === resetConfirmPassword;
      const canSubmit = resetCode.length === 6 && resetNewPassword.length >= 8 && passwordsMatch;

      return (
        <form onSubmit={handleConfirmReset} className="space-y-4">
          <div className="text-center mb-4">
            <Lock className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Enter Reset Code</h2>
            <p className="text-sm text-stone-400">
              Check your email for a 6-digit code, then choose a new password
            </p>
          </div>

          {resetInfoMessage && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{resetInfoMessage}</p>
            </div>
          )}

          {resetErrorMessage && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{resetErrorMessage}</p>
            </div>
          )}

          <div>
            <label htmlFor="resetCode" className="block text-sm font-medium text-stone-300 mb-1">
              Reset Code
            </label>
            <input
              id="resetCode"
              type="text"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-center text-2xl tracking-widest placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              maxLength={6}
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="resetNewPassword" className="block text-sm font-medium text-stone-300 mb-1">
              New Password
            </label>
            <input
              id="resetNewPassword"
              type="password"
              value={resetNewPassword}
              onChange={(e) => setResetNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              minLength={8}
              required
            />
          </div>

          <div>
            <label htmlFor="resetConfirmPassword" className="block text-sm font-medium text-stone-300 mb-1">
              Confirm Password
            </label>
            <input
              id="resetConfirmPassword"
              type="password"
              value={resetConfirmPassword}
              onChange={(e) => setResetConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              minLength={8}
              required
            />
            {resetConfirmPassword && resetNewPassword !== resetConfirmPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          <p className="text-stone-500 text-xs">
            Minimum 16 characters with uppercase, lowercase, numbers, and symbols
          </p>

          <button
            type="submit"
            disabled={isLoading || !canSubmit}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className={`text-stone-400 hover:${accentTextClass} transition-colors disabled:opacity-50`}
            >
              Resend code
            </button>
            <button
              type="button"
              onClick={backToSignIn}
              className={`text-stone-400 hover:${accentTextClass} transition-colors`}
            >
              Back to sign in
            </button>
          </div>
        </form>
      );
    }

    if (recoveryCodes) {
      return (
        <div className="space-y-4">
          <div className="text-center mb-2">
            <ShieldCheck className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Save your recovery codes</h2>
            <p className="text-sm text-stone-400">
              If you ever lose your phone, one of these codes gets you back in. Each code works once.
              This is the only time they are shown.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 bg-dark-900 border border-dark-600 rounded-lg p-4">
            {recoveryCodes.map((c) => (
              <code key={c} className="text-white font-mono text-sm text-center select-all">{c}</code>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={copyCodes}
              className="min-h-[44px] flex items-center justify-center gap-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-stone-200 hover:border-stone-500 transition-colors"
            >
              <Copy className="w-4 h-4" /> {codesCopied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={downloadCodes}
              className="min-h-[44px] flex items-center justify-center gap-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-stone-200 hover:border-stone-500 transition-colors"
            >
              <Download className="w-4 h-4" /> Download
            </button>
          </div>

          <p className="text-stone-500 text-xs">
            Good places: your password manager (1Password, Bitwarden) or printed and kept somewhere safe.
            Not on the same phone as your authenticator.
          </p>

          <button
            type="button"
            onClick={() => setCodesSaved(!codesSaved)}
            className="w-full min-h-[44px] flex items-center gap-3 text-left text-sm text-stone-300"
          >
            <span className={`w-5 h-5 flex-shrink-0 rounded border flex items-center justify-center ${codesSaved ? 'bg-red-600 border-red-600' : 'border-dark-600 bg-dark-700'}`}>
              {codesSaved && <CheckCircle2 className="w-4 h-4 text-white" />}
            </span>
            I have saved these codes somewhere safe
          </button>

          <button
            type="button"
            disabled={!codesSaved}
            onClick={acknowledgeRecoveryCodes}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Continue to {product.adminTitle} Super Admin
          </button>
        </div>
      );
    }

    if (emailCodeRequired) {
      return (
        <form onSubmit={handleEmailCode} className="space-y-4">
          <div className="text-center mb-4">
            <Mail className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Check your email</h2>
            <p className="text-sm text-stone-400">
              To confirm it&rsquo;s you, we emailed a one-time code to{' '}
              <span className="text-stone-200">{emailCodeDestination || 'your email address'}</span>.
              Next you&rsquo;ll set up your authenticator app.
            </p>
          </div>

          <input
            type="text"
            inputMode="numeric"
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="Code from email"
            className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-center text-2xl tracking-widest placeholder:text-stone-500 placeholder:text-base placeholder:tracking-normal focus:outline-none ${inputFocusClass}`}
            maxLength={8}
            autoFocus
            required
          />

          <button
            type="submit"
            disabled={isLoading || emailCode.length < 6}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Verifying...' : 'Verify & continue'}
          </button>
          <p className="text-stone-500 text-xs text-center">
            The code expires in 3 minutes. Didn&rsquo;t get it? Check spam, or go back and sign in again for a new one.
          </p>
        </form>
      );
    }

    if (mfaRequired && showRecovery) {
      return (
        <form onSubmit={handleRecovery} className="space-y-4">
          <div className="text-center mb-4">
            <LifeBuoy className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Lost your authenticator?</h2>
            <p className="text-sm text-stone-400">
              Enter one of the recovery codes you saved when you set up two-step sign-in.
            </p>
          </div>

          {recoveryError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{recoveryError}</p>
            </div>
          )}

          <input
            type="text"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().slice(0, 11))}
            placeholder="XXXXX-XXXXX"
            className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-center text-xl font-mono tracking-widest placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
            autoFocus
            required
          />

          <button
            type="submit"
            disabled={recoveryBusy || recoveryCode.replace(/[^A-Z0-9]/g, '').length !== 10}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {recoveryBusy ? 'Checking...' : 'Use recovery code'}
          </button>

          <div className="bg-dark-700/60 border border-dark-600 rounded-lg p-3 text-xs text-stone-400 space-y-1">
            <p className="text-stone-300 font-medium">What happens next</p>
            <p>1. We email you a one-time code. 2. You set up your authenticator again. 3. You get new recovery codes.</p>
            <p className="pt-1">
              <span className="text-stone-300">No recovery codes?</span> Ask another super admin to reset your
              two-step sign-in (Staff &rarr; your name &rarr; Reset two-step sign-in).
            </p>
          </div>

          <button
            type="button"
            onClick={() => { setShowRecovery(false); setRecoveryError(null); }}
            className={`w-full min-h-[44px] text-center text-sm text-stone-400 hover:${accentTextClass} transition-colors`}
          >
            Back to authenticator code
          </button>
        </form>
      );
    }

    if (mfaRequired) {
      return (
        <form onSubmit={handleMFA} className="space-y-4">
          <div className="text-center mb-4">
            <Key className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Enter MFA Code</h2>
            <p className="text-sm text-stone-400">
              Open your authenticator app and enter the 6-digit code
            </p>
          </div>

          <div>
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-center text-2xl tracking-widest placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              maxLength={6}
              autoFocus
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || mfaCode.length !== 6}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </button>

          <button
            type="button"
            onClick={() => { setShowRecovery(true); setRecoveryError(null); }}
            className={`w-full min-h-[44px] text-center text-sm text-stone-400 hover:${accentTextClass} transition-colors`}
          >
            Lost your phone or authenticator app?
          </button>
        </form>
      );
    }

    if (mfaSetupRequired) {
      return (
        <form onSubmit={handleMfaSetup} className="space-y-3 sm:space-y-4">
          {/* Compact on phones: icon beside the title, short copy, smaller QR. */}
          <div className="flex items-center justify-center gap-2 sm:block sm:text-center sm:mb-2">
            <Smartphone className={`w-6 h-6 sm:w-12 sm:h-12 ${accentTextClass} sm:mx-auto sm:mb-2 shrink-0`} />
            <h2 className="text-base sm:text-lg font-semibold text-white">Set up your authenticator</h2>
          </div>

          <ol className="text-xs sm:text-sm text-stone-300 space-y-1 list-decimal list-inside">
            <li>Open 1Password, Google or Microsoft Authenticator.</li>
            <li>Scan the code. It appears as <span className="text-white font-medium">{product.adminTitle} Super Admin</span>.</li>
            <li>Enter the 6-digit code it shows.</li>
          </ol>
          <p className="hidden sm:block text-stone-500 text-xs">
            Tip: an app that backs up to the cloud means a new phone won&rsquo;t lock you out.
          </p>

          {mfaSetupSecret && (() => {
            // Name the authenticator entry so it is recognisable later — the old
            // "PlatformAdmin" label is how entries got lost. Dev/prod differ so
            // both can live side by side in one app.
            const issuer = `${product.adminTitle} Super Admin${window.location.hostname.includes('dev') || window.location.hostname === 'localhost' ? ' (dev)' : ''}`;
            const totpUri = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountEmail || 'admin')}?secret=${mfaSetupSecret}&issuer=${encodeURIComponent(issuer)}`;
            return (
              <div className="space-y-2 sm:space-y-3">
                <div className="bg-white rounded-lg p-2.5 sm:p-4 w-fit mx-auto">
                  <QRCodeSVG value={totpUri} size={180} className="block w-32 h-32 sm:w-44 sm:h-44" />
                </div>
                <details className="bg-dark-700 border border-dark-600 rounded-lg">
                  <summary className="px-4 min-h-[44px] flex items-center text-xs text-stone-400 cursor-pointer">
                    Can't scan? Enter key manually
                  </summary>
                  <p className="px-4 pb-3 text-white font-mono text-xs break-all select-all bg-dark-900 rounded mx-3 mb-3 p-2">
                    {mfaSetupSecret}
                  </p>
                </details>
              </div>
            );
          })()}

          <div>
            <label htmlFor="setupCode" className="sr-only">
              Verification code
            </label>
            <input
              id="setupCode"
              type="text"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              className={`w-full px-4 py-2.5 sm:py-3 bg-dark-700 border border-dark-600 rounded-lg text-white text-center text-xl sm:text-2xl tracking-widest placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              maxLength={6}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || setupCode.length !== 6}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Verifying...' : 'Verify & Complete Setup'}
          </button>
        </form>
      );
    }

    if (newPasswordRequired) {
      return (
        <form onSubmit={handleNewPassword} className="space-y-4">
          <div className="text-center mb-4">
            <Lock className={`w-12 h-12 ${accentTextClass} mx-auto mb-2`} />
            <h2 className="text-lg font-semibold text-white">Set New Password</h2>
            <p className="text-sm text-stone-400">
              Please set a new password to complete your first login
            </p>
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-stone-300 mb-1">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              minLength={8}
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-stone-300 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
              minLength={8}
              required
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          <p className="text-stone-500 text-xs">
            Minimum 16 characters with uppercase, lowercase, numbers, and symbols
          </p>

          <button
            type="submit"
            disabled={isLoading || newPassword.length < 8 || newPassword !== confirmPassword}
            className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Setting password...' : 'Set Password & Continue'}
          </button>
        </form>
      );
    }

    // Default: Login form
    return (
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-stone-300 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={product.emailPlaceholder}
            className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="password" className="block text-sm font-medium text-stone-300">
              Password
            </label>
            <button
              type="button"
              onClick={openForgotPassword}
              className={`text-xs text-stone-400 hover:${accentTextClass} transition-colors`}
            >
              Forgot password?
            </button>
          </div>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className={`w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder:text-stone-500 focus:outline-none ${inputFocusClass}`}
            required
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 ${btnClass} text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    );
  };

  // Animated background tint colors per product. Crimson for Ruby Till
  // matches the rest of the brand; the default purple #DC143C is the same
  // shade as the existing admin-primary so the effect feels native to the
  // admin theme on cafemaster/terrakron.
  const networkColor = isRubyTill ? '#DC143C' : '#DC143C';

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated atom-style network background — same effect as the Ruby Till
          landing-page hero, recoloured per product so it matches the admin feel. */}
      <NodeNetwork count={26} color={networkColor} />
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-4 sm:mb-8">
          <div className="w-16 h-16 rounded-2xl bg-neutral-900 ring-2 ring-red-400/30 shadow-lg flex items-center justify-center mx-auto mb-4">
            <product.Icon size={36} className={`w-8 h-8 ${accentTextClass}`} />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            {product.brandHeading ?? product.adminTitle}
          </h1>
          <p className="text-stone-500 text-xs uppercase tracking-widest mt-2">Super Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-dark-800 rounded-xl border border-dark-700 p-4 sm:p-6">
          {/* Error Alert - the reset-password steps render their own inline
              banners below, so this only applies to sign-in/MFA/new-password
              (avoids ever surfacing a raw Cognito message, e.g. an unknown
              email, from the reset flow). */}
          {resetStep === 'idle' && error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {resetStep === 'idle' && isConsolePath(from) && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-amber-300">
              <Terminal className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">The issue console runs only in DEV. Sign in to the dev admin to open it.</p>
            </div>
          )}

          {resetStep === 'idle' && signinNotice && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{signinNotice}</p>
            </div>
          )}

          {renderForm()}
        </div>

        {/* Security Notice */}
        <p className="text-center text-stone-500 text-xs mt-6">
          Two-step sign-in is required for every super admin account.
          <br />
          Lost your phone? Use a recovery code, or ask another super admin to reset it.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
